"use client"

import { useState, useEffect, useContext, useRef, useMemo } from "react"
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Fade,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  Slide,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import DeleteIcon from "@mui/icons-material/Delete"
import { ErpContext } from "../../context/ErpContext.js"
import { getLocaleConfig, formatInputWithSeparators, parseLocalizedNumber } from "../../utils/numeros.js"
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt"
import ModalCrearEntidad from "../adminComponent/ModalCrearEntidad.js"
import InventoryIcon from "@mui/icons-material/Inventory"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PriceCheckIcon from "@mui/icons-material/PriceCheck"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import HailIcon from '@mui/icons-material/Hail'

// Componente de transición para las alertas
function SlideTransition(props) {
  return <Slide {...props} direction="up" />
}

const normalizeProveedorText = (value) =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

export default function ItemWithThreeTables({ onClose, selectedItem = null }) {
  const {
    getUbicacion,
    ubicaciones,
    getEntidadByTipoEntidad,
    entidadesTipoEntidad,
    getEntidadesClientesTresLetras,
    entidadesClientesTresLetras,
    itemAtributoServicioNoEliminados,
    getItemAtributoServicioNoEliminados,
    postItem,
    postItemUbicacion,
    postListaMonto,
    getUsuario,
    usuarios,
    getItemByCodigoItem,
    postEntidad,
    getEntidadByID,
    isNavbarCollapsed,
    getParametrosGlobales,
    // Funciones adicionales para edición
    updateItem,
    updateItemUbicaciones,
    getItemUbicacionFilter,
    itemUbicacionFilter,
    getListaMontosByIdItem,
    updateListaDeMonto,
    formatoNumericoGlobal,
  } = useContext(ErpContext)

  // Determinar si estamos en modo edición
  const isEditMode = selectedItem !== null

  const theme = useTheme()

  // Configuración de locale para formateo numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // -- Estados del encabezado
  const [codigoItem, setCodigoItem] = useState("")
  const [descripcionItem, setDescripcionItem] = useState("")
  const [descripcionServicio, setDescripcionServicio] = useState("")
  const [pluBalanzaItem, setPluBalanzaItem] = useState("")
  const [esActividad, setEsActividad] = useState(false) // Checkbox para esActividad
  const [searchTextCliente, setSearchTextCliente] = useState("") // Texto de búsqueda
  const [proveedorGenerico, setProveedorGenerico] = useState(null) // Proveedor genérico (id=2) usado como valor por defecto
  // eslint-disable-next-line no-unused-vars
  const setProveedoresDisponibles = () => {} // compatibilidad temporal con bloque legado
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null) // Usuario seleccionado
  const [openModal, setOpenModal] = useState(false)
  
  // Estado para marcar item como eliminado (solo en edición)
  const [itemEliminado, setItemEliminado] = useState(false)
  
  // Estado para loading de datos en edición
  const [isLoadingData, setIsLoadingData] = useState(false)

  // Atributos globales (hasta 10) que se completan una sola vez
  const [atributosForm, setAtributosForm] = useState([])

  // Manejo del "Item Creado" para mostrar las tablas
  const [isItemCreated, setIsItemCreated] = useState(false)

  // Estados para alertas y carga
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState("")
  const [alertSeverity, setAlertSeverity] = useState("success")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [entidadCreatedAlert, setEntidadCreatedAlert] = useState(false)
  const [entidadMessage, setEntidadMessage] = useState("")
  const [parametrosGlobales, setParametrosGlobales] = useState({})

  const showPluField = parametrosGlobales.pluBalanza === "1"
  const headerColumnSize = showPluField ? 3 : 4

  // Estado derivado para ubicaciones seleccionadas en la tabla de stock
  const [stockUbicaciones, setStockUbicaciones] = useState([])

  // -------------------------------------
  //  TABLA #1: STOCK
  // -------------------------------------
  const [stockRows, setStockRows] = useState([])
  const loadedItemIdRef = useRef(null) // Evita recargar datos en cada re-render del context

  // Efecto para actualizar las ubicaciones disponibles basadas en la tabla de stock
  useEffect(() => {
    // Extraer las ubicaciones seleccionadas de las filas de stock
    const ubicacionesSeleccionadas = stockRows
      .filter((row) => row.ubicacion) // Filtrar solo las filas con ubicación seleccionada
      .map((row) => row.ubicacion) // Obtener los IDs de ubicación
      .filter((id, index, self) => self.indexOf(id) === index) // Eliminar duplicados

    // Actualizar el estado de ubicaciones disponibles para las otras tablas
    setStockUbicaciones(ubicacionesSeleccionadas)
  }, [stockRows])

  // Efecto para cargar datos iniciales
  useEffect(() => {
    async function fetchData() {
      try {
        await getUbicacion()
        await getUsuario()
        await getItemAtributoServicioNoEliminados()
        
        // Cargar proveedores por tipo (tipo 2 = proveedores) y resolver genérico
        const proveedores = await getEntidadByTipoEntidad(2)
        let provGenerico = (proveedores || []).find((proveedor) => Number(proveedor.id) === 2) || null
        if (!provGenerico) {
          provGenerico = await getEntidadByID(2)
        }
        if (provGenerico) {
          setProveedorGenerico(provGenerico)
        }
        
        const parametros = await getParametrosGlobales(["pluBalanza"])
        if (parametros) {
          setParametrosGlobales(parametros)
        }
      } catch (error) {
        console.error("Error al cargar datos:", error)
        showAlert("Error al cargar datos iniciales", "error")
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1) // Llama a la API
    }
  }, [searchTextCliente]) //Este paramatro searchText condiciona al useEffect, detectando sus cambios. Solo se ejecuta si cambia el valor del input de searchText.

  // NOTA: La búsqueda de proveedores se maneja directamente en el onInputChange del Autocomplete
  // para evitar re-renders innecesarios que pueden eliminar filas recién agregadas

  // Al cambiar itemAtributoServicioNoEliminados, inicializar atributosForm
  useEffect(() => {
    if (itemAtributoServicioNoEliminados?.length > 0) {
      // Si estamos en modo edición, usar los valores del item seleccionado
      if (isEditMode && selectedItem?.activeAttributes) {
        const inicial = itemAtributoServicioNoEliminados.map((attr) => ({
          id: attr.id,
          descripcion: attr.descripcion,
          valor: selectedItem.activeAttributes[`itemDatoAtributo${attr.id}`] || "",
        }))
        setAtributosForm(inicial)
      } else {
        // Modo creación: valores vacíos
        const inicial = itemAtributoServicioNoEliminados.map((attr) => ({
          id: attr.id,
          descripcion: attr.descripcion,
          valor: "",
        }))
        setAtributosForm(inicial)
      }
    }
  }, [itemAtributoServicioNoEliminados, isEditMode, selectedItem])

  // Efecto para cargar datos del servicio cuando estamos en modo edición
  useEffect(() => {
    const loadItemData = async () => {
      if (!isEditMode || !selectedItem?.idItem) return

      // Si ya cargamos este servicio, solo aseguramos que las opciones incluyan genérico + proveedores actuales
      if (loadedItemIdRef.current === selectedItem.idItem) {
        const opciones = []
        stockRows.forEach((row) => {
          if (row.proveedorObj && row.proveedorObj.id !== 2 && !opciones.some((p) => p.id === row.proveedorObj.id)) {
            opciones.push(row.proveedorObj)
          }
        })
        if (opciones.length > 0) {
          setProveedoresDisponibles(opciones)
        }
        return
      }

      loadedItemIdRef.current = selectedItem.idItem
      
      setIsLoadingData(true)
      try {
        // Pre-popular datos básicos
        setCodigoItem(selectedItem.codigo || "")
        setDescripcionItem(selectedItem.descripcion || "")
        setDescripcionServicio(selectedItem.descripcionServicio || "")
        setEsActividad(selectedItem.esActividad || false)
        setItemEliminado(selectedItem.eliminado || false)
        
        // Cargar costos existentes desde ListaDeMontos
        // ✅ NUEVO: Construir desde ListaDeMontos para mostrar TODOS los proveedores
        console.log("🔍 Llamando a getListaMontosByIdItem con idItem:", selectedItem.idItem)
        const costosItem = await getListaMontosByIdItem(selectedItem.idItem)
        console.log("💰 Costos cargados desde getListaMontosByIdItem:", costosItem)
        console.log("📊 Cantidad de registros:", costosItem?.length || 0)
        if (costosItem && costosItem.length > 0) {
          console.log("📋 Estructura del primer registro:", costosItem[0])
          console.log("📋 Todos los registros detallados:", JSON.stringify(costosItem, null, 2))
        }
        
        if (costosItem && costosItem.length > 0) {
          // Cada registro en ListaDeMontos = 1 fila en la tabla
          const rows = costosItem.map((costo, index) => {
            const costoValue = costo.monto || "";
            return {
              id: `${costo.idUbicacion}-${costo.idEntidad}-${index}`, // ID único por combinación
              ubicacion: costo.idUbicacion,
              costo: costoValue,
              costoDisplay: costoValue ? formatInputWithSeparators(costoValue, localeConfig.locale) : "",
              idEntidad: costo.idEntidad || 2,
            };
          })
          
          // 🔧 Cargar el objeto completo de cada proveedor para cada fila
          const rowsConProveedores = await Promise.all(
            rows.map(async (row) => {
              try {
                const proveedor = await getEntidadByID(row.idEntidad)
                return {
                  ...row,
                  proveedorObj: proveedor || null, // Almacenar objeto completo
                }
              } catch (error) {
                console.error(`Error cargando proveedor ${row.idEntidad}:`, error)
                return {
                  ...row,
                  proveedorObj: null,
                }
              }
            })
          )
          
          setStockRows(rowsConProveedores)
          console.log("✅ Filas cargadas con proveedores completos:", rowsConProveedores)
          
          // Mantener proveedor genérico y los proveedores cargados disponibles
          const opcionesIniciales = []
          rowsConProveedores.forEach((row) => {
            if (row.proveedorObj && row.proveedorObj.id !== 2 && !opcionesIniciales.some((p) => p.id === row.proveedorObj.id)) {
              opcionesIniciales.push(row.proveedorObj)
            }
          })
          setProveedoresDisponibles(opcionesIniciales)
        } else {
          console.log("⚠️ No se encontraron costos en ListaDeMontos")
          setProveedoresDisponibles([])
        }
        
        // En modo edición, mostrar directamente las tablas
        setIsItemCreated(true)
        
      } catch (error) {
        console.error("Error al cargar datos del servicio:", error)
        showAlert("Error al cargar datos del servicio", "error")
      } finally {
        setIsLoadingData(false)
      }
    }
    
    loadItemData()
    // getEntidadByID y getListaMontosByIdItem vienen del context y su referencia cambia en cada re-render,
    // pero no necesitamos re-ejecutar la carga por esos cambios.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedItem?.idItem, proveedorGenerico])

  // Manejar cambio en atributos del encabezado
  const handleAtributoFormChange = (id, valor) => {
    setAtributosForm((prev) => prev.map((attr) => (attr.id === id ? { ...attr, valor } : attr)))
  }

  // Función para mostrar alertas
  const showAlert = (message, severity = "success") => {
    setAlertMessage(message)
    setAlertSeverity(severity)
    setAlertOpen(true)
  }

  // Función para confirmar la creación del Servicio
  // (ej: validas si se llenó código y descripción)
  const handleCrearItem = async () => {
    if (!codigoItem /*|| !descripcionItem*/) {
      showAlert("Por favor completa Código ", "warning")
      return
    }

    try {
      const existeItem = await getItemByCodigoItem(codigoItem)
      if (existeItem) {
        showAlert("Ya existe un servicio con ese código", "error")
        return
      }

      // Se marca como creado para mostrar las 3 tablas
      setIsItemCreated(true)
      showAlert("Servicio confirmado. Complete las tablas para finalizar.", "info")
    } catch (error) {
      console.error("Error al verificar el código:", error)
      showAlert("Error al verificar el código del servicio", "error")
    }
  }

  // Función para resetear el formulario completo
  const resetForm = () => {
    // Resetear campos del encabezado
    setCodigoItem("")
    setDescripcionItem("")
    setDescripcionServicio("")
    setPluBalanzaItem("")
    setEsActividad(false) // Resetear checkbox esActividad
    setUsuarioSeleccionado(null)

    // Resetear atributos
    if (itemAtributoServicioNoEliminados?.length > 0) {
      const resetAtributos = itemAtributoServicioNoEliminados.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        valor: "",
      }))
      setAtributosForm(resetAtributos)
    }

    // Resetear tablas
    setStockRows([])
    setCostRows([])

    // Resetear estado de creación
    setIsItemCreated(false)
  }

  // Agregar fila
  const handleAddStockRow = () => {
    setStockRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        ubicacion: null,
        costo: "", // Campo para el costo del servicio
        costoDisplay: "",
        idEntidad: 2, // Por defecto: proveedor genérico
        proveedorObj: proveedorGenerico, // Almacenar objeto completo del proveedor
      },
    ])
  }
  // Copiar fila
  const handleCopyStockRow = (rowId) => {
    setStockRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId)
      if (index === -1) return prev
      const original = prev[index]
      const copia = { ...original, id: Date.now() }
      const newRows = [...prev]
      newRows.splice(index + 1, 0, copia)
      return newRows
    })
  }
  // Eliminar fila
  const handleDeleteStockRow = (rowId) => {
    setStockRows((prev) => {
      const newRows = prev.filter((r) => r.id !== rowId)

      // Si se eliminó la última fila, limpiar las filas de costo
      if (newRows.length === 0) {
        setCostRows([])
      }

      return newRows
    })
  }
  // Manejar cambios
  const handleChangeStockRow = (rowId, field, value) => {
    setStockRows((prev) => {
      const newRows = prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: value,
            }
          : row,
      )

      // Si se cambió una ubicación, verificar si hay que actualizar las filas de costo
      if (field === "ubicacion") {
        // Obtener todas las ubicaciones seleccionadas en la tabla de stock
        const ubicacionesSeleccionadas = newRows
          .filter((row) => row.ubicacion)
          .map((row) => row.ubicacion)
          .filter((id, index, self) => self.indexOf(id) === index)

        // Actualizar las filas de costo para eliminar ubicaciones que ya no están en stock
        setCostRows((prev) =>
          prev.map((row) => {
            if (row.ubicacion && !ubicacionesSeleccionadas.includes(row.ubicacion)) {
              return { ...row, ubicacion: null }
            }
            return row
          }),
        )
      }

      return newRows
    })
  }

  // -------------------------------------
  //  TABLA #2: COSTOS
  // -------------------------------------
  const [costRows, setCostRows] = useState([])
  // Agregar fila
  // Modificar la función handleAddCostRow
  const handleAddCostRow = () => {
    // Solo permitir agregar filas si hay ubicaciones en stock
    if (stockRows.length === 0) {
      showAlert("Primero debe agregar ubicaciones en la tabla de Stock", "warning")
      return
    }

    setCostRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        ubicacion: stockUbicaciones.length === 1 ? stockUbicaciones[0] : null, // Si solo hay una ubicación, seleccionarla automáticamente
        proveedor: 2, // Proveedor genérico por defecto
        costo: "",
        costoDisplay: "",
      },
    ])
  }
  // Copiar fila
  const handleCopyCostRow = (rowId) => {
    setCostRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId)
      if (index === -1) return prev
      const original = prev[index]
      const copia = { 
        ...original, 
        id: Date.now(),
        proveedor: original.proveedor || 2 // Si no tiene proveedor, usar proveedor genérico por defecto
      }
      const newRows = [...prev]
      newRows.splice(index + 1, 0, copia)
      return newRows
    })
  }
  // Eliminar fila
  const handleDeleteCostRow = (rowId) => {
    setCostRows((prev) => prev.filter((r) => r.id !== rowId))
  }
  // Manejar cambios
  const handleChangeCostRow = (rowId, field, value) => {
    setCostRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)))
  }

  const abrirModalCrearEntidad = () => {
    setOpenModal(true)
  }

  useEffect(() => {
    if (costRows.length > 0) {
      setCostRows((prev) =>
        prev.map((row) => ({
          ...row,
          proveedor: row.proveedor && entidadesTipoEntidad.find((e) => e.id === row.proveedor) ? row.proveedor : null,
        })),
      )
    }
  }, [entidadesTipoEntidad])

  const handleCreateEntidad = async (nuevaEntidad) => {
    try {
      console.log("Entidad a crear:", nuevaEntidad)

      // POST: Crea la entidad y recibe el objeto con su ID
      const entidadCreada = await postEntidad(
        nuevaEntidad.descripcion,
        nuevaEntidad.apellido,
        nuevaEntidad.telefono,
        nuevaEntidad.email,
        nuevaEntidad.dniCuitCuil,
        nuevaEntidad.direccion,
        nuevaEntidad.localidad,
        nuevaEntidad.provincia,
        nuevaEntidad.tipoEntidad,
        null, // idCondicionIva
        null, // sexo
        null, // fechaNacimiento
        null, // pais
        null, // codigoPostal
        null, // observaciones
        false, // esTutor
        null, // nombreTutor
        null, // apellidoTutor
        null, // telefonoTutor
        // DATOS BANCARIOS
        nuevaEntidad.nombreBanco || null,
        nuevaEntidad.numeroCuenta || null,
        nuevaEntidad.cbuCvuCuenta || null,
        nuevaEntidad.aliasCuenta || null,
      )

      // Actualizamos la lista de opciones en los autocompletes
      await getEntidadByTipoEntidad(2)

      if (entidadCreada.tipoEntidad === 2) {
        // PROVEEDOR: Si la última fila de Costos no tiene proveedor, la asignamos.
        setCostRows((prev) => {
          if (prev.length > 0 && !prev[prev.length - 1].proveedor) {
            const newRows = [...prev]
            const lastRowId = newRows[newRows.length - 1].id

            // Forzamos la actualización del Autocomplete de proveedor
            handleChangeCostRow(lastRowId, "proveedor", entidadCreada.id)

            newRows[newRows.length - 1] = {
              ...newRows[newRows.length - 1],
              proveedor: entidadCreada.id,
            }
            return newRows
          } else {
            // Si todas las filas ya tienen proveedor, agregamos una nueva fila con la entidad seleccionada
            return [
              ...prev,
              {
                id: Date.now(),
                ubicacion: null,
                proveedor: entidadCreada.id,
                costo: "",
                costoDisplay: "",
              },
            ]
          }
        })
        setEntidadMessage(`Proveedor ${entidadCreada.descripcion} creado con éxito`)
      }

      // Mostrar alerta de éxito
      setEntidadCreatedAlert(true)
      setTimeout(() => setEntidadCreatedAlert(false), 4000)

      // Cerrar el modal
      setOpenModal(false)
    } catch (error) {
      console.error("Error al crear la entidad:", error)
      showAlert("Error al crear la entidad", "error")
    }
  }

  const handleItemSubmit = async (e) => {
    e.preventDefault()

    // Validaciones
    if (!codigoItem) {
      showAlert("Por favor complete el nombre del servicio", "warning")
      return
    }

    if (stockRows.length === 0) {
      showAlert("Debe agregar al menos una fila de stock", "warning")
      return
    }

    // Validar que todas las filas tengan datos completos
    const stockIncompleto = stockRows.some((row) => !row.ubicacion)
    const costosIncompleto = costRows.some((row) => !row.ubicacion || !row.proveedor || row.costo === "")

    if (stockIncompleto || costosIncompleto) {
      showAlert("Hay filas con información incompleta en las tablas", "warning")
      return
    }

    setIsSubmitting(true)

    try {
      if (isEditMode) {
        // ========== MODO EDICIÓN ==========
        console.log("Actualizando servicio...", selectedItem.idItem)
        
        // 1. Actualizar datos básicos del servicio
        await updateItem(
          selectedItem.idItem,
          descripcionItem || codigoItem, // Si no hay descripción, usar el nombre
          codigoItem,
          null, // codigoScanner no aplica para servicios
          atributosForm,
          itemEliminado,
          null, // pluBalanza no aplica para servicios
          descripcionServicio,
          esActividad
        )
        
        // 2. Actualizar ItemUbicacion (solo ubicaciones únicas)
        const ubicacionesUnicas = [...new Set(stockRows.map(row => row.ubicacion))]
        const ubicacionesPayload = ubicacionesUnicas.map((idUbicacion) => ({
          idUbicacion: idUbicacion,
          inventario: 0, // Servicios siempre tienen inventario 0
          stockMinimo: 0,
        }))
        console.log("📍 Actualizando ubicaciones únicas:", ubicacionesPayload)
        await updateItemUbicaciones(selectedItem.idItem, ubicacionesPayload)
        
        // 3. Actualizar ListaDeMontos (cada fila = un registro con su proveedor)
        console.log("💰 Actualizando costos - Total de filas:", stockRows.length)
        for (const row of stockRows) {
          // Permitir costo 0 o vacío (tratarlo como 0)
          const costoNum = row.costo && row.costo !== "" && row.costo !== null && row.costo !== undefined
            ? Number.parseFloat(row.costo)
            : 0  // Si está vacío, usar 0
          
          const idEntidadSeleccionada = row.idEntidad || 2
          
          if (!isNaN(costoNum) && costoNum >= 0) {  // Permitir costos >= 0
            console.log(`  → Actualizando: Ubicación ${row.ubicacion} - Entidad ${idEntidadSeleccionada} - Costo $${costoNum}`)
            await updateListaDeMonto({
              idItem: selectedItem.idItem,
              idUbicacion: Number.parseInt(row.ubicacion, 10),
              idEntidad: idEntidadSeleccionada,
              monto: costoNum
            })
          }
        }
        console.log("✅ Actualización de costos completada")
        
        // Mostrar alerta de éxito
        showAlert(`Servicio ${codigoItem} actualizado exitosamente`, "success")
        
        // Cerrar modal si existe la función onClose
        if (onClose) {
          setTimeout(() => onClose(), 1500)
        }
        
      } else {
        // ========== MODO CREACIÓN ==========
        console.log("Creando item...")
        const response = await postItem(
          descripcionItem || codigoItem, // Si no hay descripción, usar el nombre
          codigoItem,
          atributosForm,
          null, // codigoScanner no aplica para servicios
          showPluField ? pluBalanzaItem : null,
          true, // esServicio: true
          descripcionServicio, // descripcionServicio
          esActividad // esActividad
        )
        // Obtener el último item creado
        const newItemId = response.data.id
        console.log("Nuevo item creado con ID: " + newItemId)

        // Agrupar stockRows por ubicación para evitar duplicados en ItemUbicacion
        const ubicacionesUnicas = new Set()
        const costosAgrupados = []

        console.log("📊 Procesando stockRows:", stockRows.length, "filas")
        
        for (const row of stockRows) {
          // Registrar ubicación única
          ubicacionesUnicas.add(row.ubicacion)
          
          // Guardar costo con su entidad (permitir costo 0 o vacío)
          const costoNum = row.costo && row.costo !== "" 
            ? Number.parseFloat(row.costo) 
            : 0  // Si está vacío, usar 0
          
          const idEntidadSeleccionada = row.idEntidad || 2
          
          if (!isNaN(costoNum) && costoNum >= 0) {  // Permitir costos >= 0
            costosAgrupados.push({
              ubicacion: row.ubicacion,
              entidad: idEntidadSeleccionada,
              costo: costoNum
            })
          }
        }

        console.log(`📍 Ubicaciones únicas detectadas: ${ubicacionesUnicas.size}`)
        console.log(`💰 Costos a crear: ${costosAgrupados.length}`)

        // 1. Crear ItemUbicacion solo UNA VEZ por cada ubicación única
        for (const ubicacionId of ubicacionesUnicas) {
          console.log(`  → Creando ItemUbicacion: Item ${newItemId} - Ubicación ${ubicacionId}`)
          await postItemUbicacion(newItemId, ubicacionId, 0, 0)
        }

        // 2. Crear ListaDeMontos para CADA combinación de ubicación + entidad
        for (const costo of costosAgrupados) {
          console.log(`  → Creando ListaMonto: Ubicación ${costo.ubicacion} - Entidad ${costo.entidad} - Costo $${costo.costo}`)
          await postListaMonto(
            newItemId,
            Number.parseInt(costo.ubicacion, 10),
            costo.entidad,
            costo.costo
          )
        }
        
        console.log("✅ Creación completada exitosamente")

        // Crear costos adicionales (para proveedores específicos)
        for (const costoLista of costRows) {
          await postListaMonto(
            newItemId,
            Number.parseInt(costoLista.ubicacion, 10), // Convierte a entero
            Number.parseInt(costoLista.proveedor, 10), // Convierte a entero
            Number.parseFloat(costoLista.costo), // Convierte a float
          )
        }

        // Mostrar alerta de éxito
        showAlert(`Servicio ${codigoItem} creado exitosamente`, "success")

        // Resetear el formulario
        resetForm()
      }
    } catch (error) {
      console.error("Error en la creación/actualización del servicio:", error)
      showAlert(isEditMode ? "Error al actualizar el servicio" : "Error al crear el servicio", "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* ========== ENCABEZADO ========== */}
      <Card
        elevation={3}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
          position: "relative",
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgb(244, 147, 14)' }}>
                {isEditMode ? `Editar Servicio: ${selectedItem?.descripcion || selectedItem?.codigo}` : "Crear Servicio"}
              </Typography>
              {isEditMode && onClose && (
                <IconButton onClick={onClose} size="small" sx={{ ml: 2 }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          }
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        />

        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Fila única: Nombre + Categoría + Es Actividad + Confirmar */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "nowrap",
                  overflowX: "auto",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 2, minWidth: 360, py:0.7 }}>
                  <LocalOfferIcon sx={{ color: 'rgb(244, 147, 14)' }} />
                  <TextField
                    label="Nombre"
                    variant="outlined"
                    size="small"
                    value={codigoItem}
                    onChange={(e) => setCodigoItem(e.target.value)}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    }}
                  />
                </Box>
                <TextField
                  label="Categoria"
                  variant="outlined"
                  size="small"
                  value={atributosForm[0]?.valor || ""}
                  onChange={(e) =>
                    atributosForm[0] ? handleAtributoFormChange(atributosForm[0].id, e.target.value) : null
                  }
                  sx={{
                    flex: 1,
                    minWidth: 220,
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    ml: "auto",
                    flex: 1,
                    justifyContent: "flex-end",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={esActividad}
                        onChange={(e) => setEsActividad(e.target.checked)}
                        sx={{
                          color: 'rgb(244, 147, 14)',
                          '&.Mui-checked': {
                            color: 'rgb(244, 147, 14)',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#5b5b5f', whiteSpace: 'nowrap' }}>
                        Es Actividad
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleCrearItem}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 4,
                      boxShadow: 3,
                      backgroundColor: '#5b5b5f',
                      color: 'white',
                      whiteSpace: "nowrap",
                    }}
                  >
                    Confirmar Servicio
                  </Button>
                </Box>
              </Box>
            </Grid>
            {/* <Grid item xs={12} md={headerColumnSize}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DescriptionIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <TextField
                  label="Descripción Servicio"
                  variant="outlined"
                  size="small"
                  value={descripcionItem}
                  onChange={(e) => setDescripcionItem(e.target.value)}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
            </Grid> */}

            {/* <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <DescriptionIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, mt: 1.5 }} />
                <TextField
                  fullWidth
                  label="Detalle del Servicio"
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  value={descripcionServicio}
                  onChange={(e) => setDescripcionServicio(e.target.value)}
                  placeholder="Ej: Experiencia gastronómica con frutas, bombones y delicias artesanales."
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
            </Grid> */}

            {/* {showPluField && (
              <Grid item xs={12} md={headerColumnSize}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormatListNumberedIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                  <TextField
                    fullWidth
                    label="PLU Balanza"
                    variant="outlined"
                    size="small"
                    value={pluBalanzaItem}
                    onChange={(e) => setPluBalanzaItem(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              </Grid>
            )} */}

            {/* <Grid item xs={12} md={2}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon sx={{ color: 'rgb(244, 147, 14)', mr: 1, my: 0.5 }} />
                <Autocomplete
                  fullWidth
                  disablePortal
                  size="small"
                  options={usuarios.map((it) => ({
                    label: `${it.usuario} - ${it.nombre} - ${it.apellido}`,
                    nombre: it.nombre,
                    apellido: it.apellido,
                    id: it.id,
                    usuario: it.usuario,
                  }))}
                  value={usuarioSeleccionado}
                  onChange={(event, newValue) => {
                    setUsuarioSeleccionado(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Usuario" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                  )}
                />
              </Box>
            </Grid> */}

          </Grid>
        </CardContent>
      </Card>

      {/* ========== Sección de Tablas (solo si item creado) ========== */}
      {isItemCreated && (
        <Fade in={isItemCreated} timeout={500}>
          <Box>
            <Grid container spacing={3}>
              {/* ========== TABLA 1: DESTINOS ========== */}
              <Grid item xs={12}>
                <Card elevation={3} sx={{ borderRadius: 2, height: "100%" }}>
                  <CardHeader
                    avatar={<HailIcon sx={{color:'rgb(244, 147, 14)'}} />}
                    title={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Destinos
                        </Typography>
                        <Tooltip title="Agregar fila">
                          <IconButton onClick={handleAddStockRow} size="small" sx={{ ml: 1 }}>
                            <AddCircleOutlineIcon sx={{color:'rgb(244, 147, 14)'}} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    // subheader={
                    //   <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    //     Primero agregue destinos aquí para habilitar la tabla de costos
                    //   </Typography>
                    // }
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  />

                  <CardContent sx={{ p: 0, height: "calc(100% - 72px)" }}>
                    <TableContainer
                      sx={{
                        height: "100%",
                        overflowY: "auto",
                        display: "block",
                      }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "35%",
                              }}
                            >
                              Destino
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "30%",
                              }}
                            >
                              Proveedor
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "20%",
                              }}
                            >
                              Costo
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor: '#5b5b5f', color: 'white',
                                width: "15%",
                              }}
                            >
                              Acciones
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                <Typography color="text.secondary">
                                  No hay registros. Haga clic en + para agregar.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            stockRows.map((row) => (
                              <TableRow key={row.id} hover>
                                {/* Ubicación/Destino */}
                                <TableCell>
                                  <Autocomplete
                                    size="small"
                                    options={ubicaciones || []}
                                    getOptionLabel={(opt) => opt.descripcion}
                                    value={ubicaciones?.find((u) => u.id === row.ubicacion) || null}
                                    onChange={(e, newValue) =>
                                      handleChangeStockRow(row.id, "ubicacion", newValue ? newValue.id : null)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        label="Destino"
                                        variant="outlined"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                          },
                                        }}
                                      />
                                    )}
                                    ListboxProps={{
                                      style: {
                                        maxHeight: 200,
                                      },
                                    }}
                                  />
                                </TableCell>
                                {/* Proveedor */}
                                <TableCell>
                                  <Autocomplete
                                    size="small"
                                    options={entidadesTipoEntidad || []}
                                    getOptionLabel={(opt) => opt?.descripcion || ""}
                                    filterOptions={(options, { inputValue }) => {
                                      const needle = normalizeProveedorText(inputValue)
                                      if (!needle) return options
                                      return options.filter((option) =>
                                        normalizeProveedorText(
                                          [option?.descripcion, option?.apellido].join(" ")
                                        ).includes(needle)
                                      )
                                    }}
                                    value={entidadesTipoEntidad?.find((p) => p.id === row.idEntidad) || row.proveedorObj || null}
                                    onChange={(e, newValue) => {
                                      setStockRows((prev) => prev.map((r) =>
                                        r.id === row.id
                                          ? { ...r, idEntidad: newValue ? newValue.id : null, proveedorObj: newValue }
                                          : r
                                      ))
                                    }}
                                    onInputChange={async (event, newInputValue, reason) => {
                                      return
                                      // bloque legado desactivado - el filtrado es ahora local
                                      if (reason === 'input') {
                                        if (newInputValue.length >= 3) {
                                          try {
                                            const resultadosBusqueda = await getEntidadesClientesTresLetras(newInputValue, 2) || []
                                            let opcionesFinales = [...resultadosBusqueda]
                                            if (row.proveedorObj && row.proveedorObj.id !== 2 && !opcionesFinales.some(p => p.id === row.proveedorObj.id)) {
                                              opcionesFinales.push(row.proveedorObj)
                                            }
                                            setProveedoresDisponibles(opcionesFinales)
                                          } catch (error) {
                                            console.error("Error buscando proveedores:", error)
                                          }
                                        } else if (newInputValue.length === 0) {
                                          const opcionesIniciales = []
                                          if (row.proveedorObj && row.proveedorObj.id !== 2) {
                                            opcionesIniciales.push(row.proveedorObj)
                                          }
                                          setProveedoresDisponibles(opcionesIniciales)
                                        }
                                      }
                                      if (reason === 'clear') {
                                        setProveedoresDisponibles([])
                                      }
                                    }}
                                    renderOption={(props, option) => (
                                      <li {...props}>
                                        <Box>
                                          <Box sx={{ fontWeight: 500 }}>{option.descripcion}</Box>
                                          {(option.apellido || option.dniCuitCuil) && (
                                            <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                              {[option.apellido, option.dniCuitCuil].filter(Boolean).join(" / ")}
                                            </Box>
                                          )}
                                        </Box>
                                      </li>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        label="Proveedor"
                                        placeholder="Seleccione o escriba para filtrar"
                                        variant="outlined"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                          },
                                        }}
                                      />
                                    )}
                                    ListboxProps={{
                                      style: {
                                        maxHeight: 200,
                                      },
                                    }}
                                    clearOnBlur={false}
                                    clearOnEscape
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                  />
                                </TableCell>
                                {/* Costo */}
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="text"
                                    inputMode="decimal"
                                    label="Costo"
                                    placeholder="0 (opcional)"
                                    value={row.costoDisplay || ""}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      const { locale, decimalSeparator, thousandSeparator } = localeConfig;
                                      
                                      if (inputValue === "") {
                                        handleChangeStockRow(row.id, "costo", "");
                                        handleChangeStockRow(row.id, "costoDisplay", "");
                                        return;
                                      }
                                      
                                      const parsed = parseLocalizedNumber(inputValue, locale);
                                      if (parsed === null && inputValue.trim() !== "") return;
                                      
                                      let formatted = parsed !== null ? formatInputWithSeparators(parsed, locale) : "";
                                      
                                      // Preservar decimal al final
                                      if (inputValue.endsWith(decimalSeparator) && !formatted.endsWith(decimalSeparator)) {
                                        formatted += decimalSeparator;
                                      }
                                      
                                      handleChangeStockRow(row.id, "costo", parsed ?? "");
                                      handleChangeStockRow(row.id, "costoDisplay", formatted);
                                    }}
                                    onBlur={(e) => {
                                      const parsed = parseLocalizedNumber(e.target.value, localeConfig.locale);
                                      if (parsed !== null && parsed >= 0) {
                                        const formatted = formatInputWithSeparators(parsed, localeConfig.locale);
                                        handleChangeStockRow(row.id, "costoDisplay", formatted);
                                      }
                                    }}
                                    variant="outlined"
                                    InputProps={{
                                      startAdornment: <span style={{ marginRight: 4 }}>$</span>,
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.5,
                                      },
                                    }}
                                    inputProps={{
                                      autoComplete: "off",
                                    }}
                                  />
                                </TableCell>
                                {/* Acciones */}
                                <TableCell align="center">
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <Tooltip title="Copiar Fila">
                                      <IconButton onClick={() => handleCopyStockRow(row.id)} color="info" size="small">
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar Fila">
                                      <IconButton
                                        onClick={() => handleDeleteStockRow(row.id)}
                                        color="error"
                                        size="small"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tabla de costos ocultada temporalmente */}
              {false && (
                <Grid item xs={12} md={7}>
                  <Grid container spacing={3}>
                    {/* ========== TABLA 2: COSTOS ========== */}
                    <Grid item xs={12}>
                      <Card
                        elevation={3}
                        sx={{
                          borderRadius: 2,
                          opacity: stockRows.length === 0 ? 0.7 : 1,
                          position: "relative",
                        }}
                      >
                        {stockRows.length === 0 && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 10,
                              backgroundColor: "rgba(255,255,255,0.7)",
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Primero agregue destinos en la tabla de Destinos
                            </Typography>
                          </Box>
                        )}
                        <CardHeader
                          avatar={<AttachMoneyIcon color={stockRows.length === 0 ? "disabled" : 'rgb(244, 147, 14)'} sx={{color:'rgb(244, 147, 14)'}}  />}
                          title={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: stockRows.length === 0 ? "text.disabled" : "inherit" }}
                              >
                                Lista de Costos
                              </Typography>
                              <Tooltip
                                title={
                                  stockRows.length === 0
                                    ? "Primero agregue destinos en la tabla de Destinos"
                                    : "Agregar fila"
                                }
                              >
                                <span>
                                  <IconButton
                                    onClick={handleAddCostRow}
                                    color="primary"
                                    size="small"
                                    sx={{ ml: 1 }}
                                    disabled={stockRows.length === 0}
                                  >
                                    <AddCircleOutlineIcon sx={{color:'rgb(244, 147, 14)'}} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          }
                          sx={{
                            backgroundColor: alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.02 : 0.05),
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, stockRows.length === 0 ? 0.05 : 0.1)}`,
                          }}
                        />

                        <CardContent sx={{ p: 0 }}>
                          <TableContainer sx={{ maxHeight: 250, overflowY: "auto" }}>
                            <Table stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: "bold",
                                      backgroundColor: '#5b5b5f', color: 'white',
                                      width: "35%",
                                    }}
                                  >
                                    Destino
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: "bold",
                                      backgroundColor: '#5b5b5f', color: 'white',
                                      width: "35%",
                                    }}
                                  >
                                    Proveedor
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: "bold",
                                      backgroundColor: '#5b5b5f', color: 'white',
                                      width: "15%",
                                    }}
                                  >
                                    Costo
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: "bold",
                                      backgroundColor: '#5b5b5f', color: 'white',
                                      width: "15%",
                                    }}
                                  >
                                    Acciones
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {costRows.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                      <Typography color="text.secondary">
                                        {stockRows.length === 0
                                          ? "Primero agregue destinos en la tabla de Destinos"
                                          : "No hay registros. Haga clic en + para agregar."}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  costRows.map((row) => (
                                    <TableRow key={row.id} hover>
                                      {/* Ubicación */}
                                      <TableCell>
                                        <Autocomplete
                                          size="small"
                                          options={ubicaciones?.filter((u) => stockUbicaciones.includes(u.id)) || []}
                                          getOptionLabel={(opt) => opt.descripcion}
                                          value={ubicaciones?.find((u) => u.id === row.ubicacion) || null}
                                          onChange={(e, newValue) =>
                                            handleChangeCostRow(row.id, "ubicacion", newValue ? newValue.id : null)
                                          }
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              size="small"
                                              label="Destino"
                                              variant="outlined"
                                              sx={{
                                                "& .MuiOutlinedInput-root": {
                                                  borderRadius: 1.5,
                                                },
                                              }}
                                            />
                                          )}
                                          ListboxProps={{
                                            style: {
                                              maxHeight: 200,
                                            },
                                          }}
                                          disabled={stockRows.length === 0}
                                        />
                                      </TableCell>
                                      {/* Resto de la fila permanece igual */}
                                      {/* Proveedor */}
                                      <TableCell>
                                        <Autocomplete
                                          size="small"
                                          options={entidadesTipoEntidad || []}
                                          getOptionLabel={(opt) => opt.descripcion}
                                          value={entidadesTipoEntidad?.find((p) => p.id === row.proveedor) || null}
                                          onChange={(e, newValue) =>
                                            handleChangeCostRow(row.id, "proveedor", newValue ? newValue.id : null)
                                          }
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              size="small"
                                              label="Proveedor"
                                              variant="outlined"
                                              sx={{
                                                "& .MuiOutlinedInput-root": {
                                                  borderRadius: 1.5,
                                                },
                                              }}
                                            />
                                          )}
                                          ListboxProps={{
                                            style: {
                                              maxHeight: 200,
                                            },
                                          }}
                                          disabled={stockRows.length === 0}
                                        />
                                      </TableCell>
                                      {/* Costo */}
                                      <TableCell>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          label="Costo"
                                          value={row.costo}
                                          onChange={(e) => handleChangeCostRow(row.id, "costo", e.target.value)}
                                          InputProps={{
                                            startAdornment: (
                                              <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
                                                $
                                              </Box>
                                            ),
                                          }}
                                          variant="outlined"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 1.5,
                                            },
                                          }}
                                          disabled={stockRows.length === 0}
                                        />
                                      </TableCell>
                                      {/* Acciones */}
                                      <TableCell align="center">
                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                          <Tooltip title="Copiar Fila">
                                            <IconButton
                                              onClick={() => handleCopyCostRow(row.id)}
                                              color="info"
                                              size="small"
                                              disabled={stockRows.length === 0}
                                            >
                                              <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Eliminar Fila">
                                            <IconButton
                                              onClick={() => handleDeleteCostRow(row.id)}
                                              color="error"
                                              size="small"
                                              disabled={stockRows.length === 0}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>

                  </Grid>
                </Grid>
              )}

              {/* Botón para guardar todo y checkbox eliminación - CENTRADOS */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2, gap: 3, flexWrap: "wrap" }}>
                  {/* Checkbox de eliminación solo en modo edición */}
                  {isEditMode && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={itemEliminado}
                          onChange={(e) => setItemEliminado(e.target.checked)}
                          color="warning"
                          icon={<WarningAmberIcon />}
                          checkedIcon={<WarningAmberIcon />}
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ textTransform: "none" }}>
                          Marcar como eliminado
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        px: 2,
                        py: 1.5,
                        backgroundColor: itemEliminado ? alpha(theme.palette.warning.main, 0.1) : "transparent",
                        border: itemEliminado ? `2px solid ${theme.palette.warning.main}` : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        borderRadius: 2,
                        boxSizing: "border-box",
                      }}
                    />
                  )}

                  {/* Botón Guardar Cambios */}
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleItemSubmit}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <InventoryIcon />}
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      px: 5,
                      py: 1.5,
                      m: 0, // Margin igual al checkbox
                      boxShadow: 3,
                    }}
                  >
                    {isSubmitting ? "Procesando..." : (isEditMode ? "Guardar Cambios" : "Crear Servicio")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}

      {/* Modal para crear entidad */}
      <ModalCrearEntidad
        open={openModal}
        handleClose={() => setOpenModal(false)}
        onCreateEntidad={handleCreateEntidad}
      />

      {/* Alerta principal */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Alerta de entidad creada */}
      <Snackbar
        open={entidadCreatedAlert}
        autoHideDuration={4000}
        onClose={() => setEntidadCreatedAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          icon={<CheckCircleIcon fontSize="inherit" />}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            backgroundColor: theme.palette.success.main,
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
          }}
        >
          {entidadMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

